import { cn } from "@/lib/utils";
import { User, Paperclip, Pencil } from "lucide-react";
import { RenataAvatar } from "./RenataAvatar";
import { RenataProposalCard } from "./RenataProposal";
import { RenataRichContent, parseRichBlocks } from "./RenataRichContent";
import { RenataQuickActions } from "./RenataQuickActions";
import type { RenataMessage as RenataMessageType, RenataProposal } from "@/lib/renata/types";
import { sanitizeChatAssistantContent } from "@/lib/renata/field-draft";

interface RenataMessageProps {
  message: RenataMessageType;
  conversationId: string;
  tenantId: string;
  contextIntent?: string | null;
  onProposalResult: (
    messageId: string,
    proposal: RenataProposal,
    approved: boolean,
    result?: unknown,
  ) => void;
  onCancelProposal?: (messageId: string, proposal: RenataProposal) => void;
  onRegenerate?: () => void;
  onEditUserMessage?: (content: string) => void;
}

export function RenataMessageBubble({
  message,
  conversationId,
  tenantId,
  contextIntent,
  onProposalResult,
  onCancelProposal,
  onRegenerate,
  onEditUserMessage,
}: RenataMessageProps) {
  const isUser = message.role === "user";
  const displayContent = isUser ? message.content : sanitizeChatAssistantContent(message.content);
  const { text, blocks } = isUser ? { text: displayContent, blocks: [] } : parseRichBlocks(displayContent);

  return (
    <div className={cn("flex gap-2", isUser ? "flex-row-reverse" : "flex-row")}>
      {/* Avatar */}
      {isUser ? (
        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary text-xs text-primary-foreground">
          <User className="h-3.5 w-3.5" />
        </div>
      ) : (
        <RenataAvatar size="sm" />
      )}

      {/* Bubble */}
      <div className={cn("flex max-w-[85%] flex-col gap-1", isUser ? "items-end" : "items-start")}>
        <div
          className={cn(
            "rounded-2xl px-3 py-2 text-sm",
            isUser
              ? "bg-primary text-primary-foreground rounded-tr-sm"
              : "bg-muted text-foreground rounded-tl-sm",
          )}
        >
          {/* Attachments */}
          {message.attachments?.filter((a) => a.type !== "audio").length > 0 && (
            <div className="mb-2 flex flex-wrap gap-1">
              {message.attachments.filter((a) => a.type !== "audio").map((a) => (
                <span
                  key={a.id}
                  className="flex items-center gap-1 rounded bg-background/30 px-1.5 py-0.5 text-xs"
                >
                  <Paperclip className="h-2.5 w-2.5" />
                  {a.name}
                </span>
              ))}
            </div>
          )}
          {/* Text content */}
          <p className="whitespace-pre-wrap break-words leading-relaxed">{text}</p>
        </div>

        {/* Rich data blocks (fuera del bubble para mejor legibilidad) */}
        {blocks.length > 0 && (
          <div className="w-full space-y-2">
            {blocks.map((block, i) => (
              <RenataRichContent key={i} block={block} />
            ))}
          </div>
        )}

        {/* Quick Actions (proposals or text-improve) */}
        {!isUser && (message.proposals?.length > 0 || contextIntent) ? (
          <RenataQuickActions
            proposals={message.proposals ?? []}
            content={text}
            contextIntent={contextIntent}
            onCancelProposal={
              onCancelProposal
                ? (proposal) => onCancelProposal(message.id, proposal)
                : undefined
            }
            onRegenerate={onRegenerate}
          />
        ) : !isUser && onRegenerate ? (
          <button
            type="button"
            className="text-[11px] text-muted-foreground underline hover:text-foreground"
            onClick={onRegenerate}
          >
            Regenerar
          </button>
        ) : null}

        {/* Proposals */}
        {!isUser && message.proposals?.some((p) => p.skill_id !== "inline_field" && p.action !== "apply_field") && (
          <div className="w-full space-y-1">
            {message.proposals
              .filter((p) => p.skill_id !== "inline_field" && p.action !== "apply_field")
              .map((proposal) => (
              <div key={proposal.id} id={`renata-proposal-${proposal.id}`}>
                <RenataProposalCard
                  proposal={proposal}
                  conversationId={conversationId}
                  messageId={message.id}
                  tenantId={tenantId}
                  onResult={(updated, approved, result) =>
                    onProposalResult(message.id, updated, approved, result)
                  }
                />
              </div>
            ))}
          </div>
        )}

        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">
            {new Date(message.created_at).toLocaleTimeString("es-AR", {
              hour: "2-digit",
              minute: "2-digit",
              hour12: false,
            })}
            {message.skill_used && (
              <span className="ml-1 opacity-60">· {message.skill_used}</span>
            )}
          </span>
          {isUser && onEditUserMessage ? (
            <button
              type="button"
              className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground"
              onClick={() => onEditUserMessage(message.content)}
              title="Editar mensaje"
            >
              <Pencil className="h-3 w-3" />
              Editar
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
